import { memo, useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from 'moment-timezone';
import _isEmpty from 'lodash/isEmpty';
//Local files
import { CardItem } from './ScheduleFlows.styled';
import ScheduleAnalysesList from 'components/Analyses/ScheduleAnalyses/List/List';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useSuccess from 'hooks/useSuccess';
import useScheduleFlows from 'hooks/useScheduleFlows';
import useScheduleAnalyses from 'hooks/useScheduleAnalyses';
import { DELETE_SCHEDULE_FLOW } from 'helpers/confirmations';
import { checkEmptyString, needToUpdateField } from 'helpers';
import { scheduleFlowsSelectors } from "features/scheduleFlows";

const weekdays = {
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
  sunday: false
};

const ScheduleFlows = () => {
  const { openDeleteDialog } = useApp();
  const { setSuccess } = useSuccess();
  const { getScheduleFlow, createScheduleFlow, updateScheduleFlow } = useScheduleFlows();
  const { getScheduleAnalyses, clearLocalScheduleAnalyses } = useScheduleAnalyses();
  const id = useCustomSelector(scheduleFlowsSelectors.getScheduleFlowId);
  const scheduleFlow = useCustomSelector(scheduleFlowsSelectors.getScheduleFlowData);
  const fetching = useCustomSelector(scheduleFlowsSelectors.scheduleFlowIsFetching);
  const [pull, setPull] = useState('ehr');
  const [scheduleWeekdays, setScheduleWeekdays] = useState(weekdays);
  const [scheduleStartAt, setScheduleStartAt] = useState('00:00');
  const [scheduleEndAt, setScheduleEndAt] = useState('00:00');
  const [appointmentPrompt, setAppointmentPrompt] = useState({ value: '', error: '' });
  const [bookingPrompt, setBookingPrompt] = useState({ value: '', error: '' });
  const [patientPrompt, setPatientPrompt] = useState({ value: '', error: '' });
  const [cliniciansPrompt, setCliniciansPrompt] = useState({ value: '', error: '' });
  const [offsetPrompt, setOffsetPrompt] = useState({ value: '', error: '' });
  const [filterSlotsPrompt, setFilterSlotsPrompt] = useState({ value: '', error: '' });
  const [message, setMessage] = useState({ value: '', error: '' });
  const [noSlotsMessage, setNoSlotsMessage] = useState({ value: '', error: '' });
  const [edit, setEdit] = useState(false);
  const [firstTimeOpened, setFirstTimeOpened] = useState(true);
  
  const getTimeFromISO = (at) => {
    if (at) return at.split('T')[1].slice(0, 5);
    return '';
  }
  const timeToIso = (time) => {
    return moment.utc().format("YYYY-MM-DD") + `T${time}:00.000Z`;
  }
   
  const handleEntering = (schedule_flow) => {
    setPull(schedule_flow?.pull);
    setScheduleWeekdays(Object.fromEntries(Object.keys(weekdays).map(day => [day, schedule_flow.schedule_weekdays.includes(day)])));
    setScheduleStartAt(schedule_flow?.schedule_start_at ? getTimeFromISO(schedule_flow?.schedule_start_at) : '00:00');
    setScheduleEndAt(schedule_flow?.schedule_end_at ? getTimeFromISO(schedule_flow?.schedule_end_at) : '00:00');
    setAppointmentPrompt({ value: schedule_flow?.appointment_prompt ?? '', error: '' });
    setBookingPrompt({ value: schedule_flow?.booking_prompt ?? '', error: '' });
    setPatientPrompt({ value: schedule_flow?.patient_prompt ?? '', error: '' });
    setCliniciansPrompt({ value: schedule_flow?.clinicians_prompt ?? '', error: '' });
    setOffsetPrompt({ value: schedule_flow?.offset_prompt ?? '', error: '' });
    setFilterSlotsPrompt({ value: schedule_flow?.filter_slots_prompt ?? '', error: '' });
    setMessage({ value: schedule_flow?.message ?? '', error: '' });
    setNoSlotsMessage({ value: schedule_flow?.no_slots_message ?? '', error: '' });
  };
  const handleExited = () => {
    setPull('ehr');
    setScheduleWeekdays(weekdays);
    setScheduleStartAt('00:00');
    setScheduleEndAt('00:00');
    setAppointmentPrompt({ value: '', error: '' });
    setBookingPrompt({ value: '', error: '' });
    setPatientPrompt({ value: '', error: '' });
    setCliniciansPrompt({ value: '', error: '' });
    setOffsetPrompt({ value: '', error: '' });
    setFilterSlotsPrompt({ value: '', error: '' });
    setMessage({ value: '', error: '' });
    setNoSlotsMessage({ value: '', error: '' });
    setEdit(false);
  };
  const handleSave = () => {
    let hasError = false;

    const selectedDays = Object.keys(scheduleWeekdays).filter(day => scheduleWeekdays[day]);

    if (!checkEmptyString(cliniciansPrompt.value)) {
      setCliniciansPrompt(prev => ({ ...prev, error: `Clinicians Prompt can't be empty` }) );
      hasError = true;
    }
    if (!checkEmptyString(filterSlotsPrompt.value)) {
      setFilterSlotsPrompt(prev => ({ ...prev, error: `Filter Slots Prompt can't be empty` }) );
      hasError = true;
    }

    if (!checkEmptyString(noSlotsMessage.value)) {
      setNoSlotsMessage(prev => ({ ...prev, error: `No Slots Message can't be empty` }) );
      hasError = true;
    }

    if (hasError) return;

    const schedule_flow = {
      ...(needToUpdateField(scheduleFlow?.pull, pull) && { pull }),
      ...(pull === 'schedule' && needToUpdateField(scheduleFlow?.schedule_weekdays, selectedDays) && { schedule_weekdays: selectedDays }),
      ...(pull === 'schedule' && needToUpdateField(getTimeFromISO(scheduleFlow?.schedule_start_at), scheduleStartAt) && { schedule_start_at:timeToIso(scheduleStartAt) }),
      ...(pull === 'schedule' && needToUpdateField(getTimeFromISO(scheduleFlow?.schedule_end_at), scheduleEndAt) && { schedule_end_at: timeToIso(scheduleEndAt) }),
      ...(needToUpdateField(scheduleFlow?.appointment_prompt, appointmentPrompt.value) && { appointment_prompt: appointmentPrompt.value }),
      ...(needToUpdateField(scheduleFlow?.booking_prompt, bookingPrompt.value) && { booking_prompt: bookingPrompt.value }),
      ...(needToUpdateField(scheduleFlow?.patient_prompt, patientPrompt.value) && { patient_prompt: patientPrompt.value }),
      ...(needToUpdateField(scheduleFlow?.clinicians_prompt, cliniciansPrompt.value) && { clinicians_prompt: cliniciansPrompt.value }),
      ...(needToUpdateField(scheduleFlow?.offset_prompt, offsetPrompt.value) && { offset_prompt: offsetPrompt.value }),
      ...(needToUpdateField(scheduleFlow?.filter_slots_prompt, filterSlotsPrompt.value) && { filter_slots_prompt: filterSlotsPrompt.value }),
      ...(needToUpdateField(scheduleFlow?.message, message.value) && { message: message.value }),
      ...(needToUpdateField(scheduleFlow?.no_slots_message, noSlotsMessage.value) && { no_slots_message: noSlotsMessage.value })
    };

    if (!_isEmpty(schedule_flow)) {
      if (!!id) {
        updateScheduleFlow(schedule_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully updated');
          }
        })
        .finally(() => setEdit(false))
      } else {
        createScheduleFlow(schedule_flow)
        .then(({ error }) => {
          if (!error) {
            setSuccess('Flow successfully created');
          }
        })
      }
    } 
  }
  const handleDelete = () => openDeleteDialog({ id: '', type: DELETE_SCHEDULE_FLOW });
  const handleDayChange = (event) => {
    setScheduleWeekdays({ ...scheduleWeekdays, [event.target.name]: event.target.checked });
  };
 
  const fetchScheduleAnalyses = useCallback(() => {
    getScheduleAnalyses({ offset: 0 })
  }, [getScheduleAnalyses]);
  
  useEffect(() => {
    if (!id) {
      handleExited();
      clearLocalScheduleAnalyses();
    };
  }, [id, clearLocalScheduleAnalyses]);
  useEffect(() => {
    if (firstTimeOpened) {
      getScheduleFlow()
      .then(({ payload, error }) => {
        if (!error) {
          const { schedule_flow } = payload;
          handleEntering(schedule_flow);
          fetchScheduleAnalyses();
        }
      })
      .finally(() => setFirstTimeOpened(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getScheduleFlow, fetchScheduleAnalyses, firstTimeOpened]); 

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', p: 5 }}>
      <Container maxWidth={'sm'}>
        <Card sx={{ p: 2 }}>
          <CardItem>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>Configuration</Typography>
            <Box>
              {!id && <Button variant='outlined' disabled={fetching} onClick={handleSave}>Create</Button>}
              {id && !edit && <Button variant='outlined' disabled={fetching} onClick={() => setEdit(true)}>Edit</Button>}
              {id && edit && <Button variant='outlined' disabled={fetching} sx={{ mr: '5px' }} onClick={handleSave}>Update</Button>}
              {id && edit && <Button variant='outlined' sx={{ color: '#E30000', borderColor: '#E30000' }} disabled={fetching} onClick={handleDelete}>Delete</Button>}
            </Box>
          </CardItem>
          <CardItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
             <TextField
              fullWidth
              margin='dense'
              select
              label='Pull data'
              value={pull}
              onChange={({ target: { value } }) => setPull(value)}
              disabled={fetching || (!!id && !edit)}
            >
              <MenuItem key={'ehr'} value={'ehr'}>EHR</MenuItem>
              <MenuItem key={'schedule'} value={'schedule'}>Schedule</MenuItem>
            </TextField>
            {pull === 'schedule' && <FormGroup row sx={{ justifyContent: 'center' }}>
              {Object.keys(scheduleWeekdays).map(day => (
                <FormControlLabel
                  key={day}
                  control={
                    <Checkbox
                      checked={scheduleWeekdays[day]}
                      onChange={handleDayChange}
                      name={day}
                      disabled={fetching || (!!id && !edit)}
                    />
                  }
                  label={day.charAt(0).toUpperCase() + day.slice(1)}
                />
              ))}
            </FormGroup>}
            {pull === 'schedule' && <Box sx={{ display: 'flex', width: '100%' }}>
              <TextField
                fullWidth
                margin="dense"
                disabled={fetching || (!!id && !edit)}
                label="Start time"
                type="time"
                sx={{ mr: '5px' }}
                value={scheduleStartAt}
                onChange={(e) => setScheduleStartAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                margin="dense"
                disabled={fetching || (!!id && !edit)}
                label="End time"
                type="time"
                value={scheduleEndAt}
                onChange={(e) => setScheduleEndAt(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>}
            <TextField
              fullWidth
              label='Prompt for extracting appointment data'
              multiline
              margin='dense'
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={appointmentPrompt.error}
              error={!!appointmentPrompt.error}
              value={appointmentPrompt.value}
              onChange={({ target: { value } }) => setAppointmentPrompt({ value, error: '' })}
            />
            <TextField
              fullWidth
              label='Prompt for extracting patient data'
              multiline
              margin='dense'
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={patientPrompt.error}
              error={!!patientPrompt.error}
              value={patientPrompt.value}
              onChange={({ target: { value } }) => setPatientPrompt({ value, error: '' })}
            />
            <Accordion sx={{ width: '100%' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel3-content"
                id="panel3-header"
              >
                <Typography>Variables for clinicians prompt</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                  }}
                >
                {"\"injury\"\n" +
                  "\"diagnosis\"\n" +
                  "\"injury_location\"\n" +
                  "\"fracture\"\n" +
                  "\"surgery\"\n" +
                  "\"acl\"\n" +
                  "\"joint_replacement\"\n" +
                  "\"spine_injury_location\"\n" +
                  "\"insurance_type\"\n" +
                  "\"insurance_carrier\"\n" +
                  "\"insurance_number\"\n" +
                  "\"preferred_clinician\"\n" +
                  "\"appointment_type\"\n" +
                  "\"age\"\n" +
                  "\"preferred_location\"\n" +
                  "\"patient_type\"\n" +
                  "\"patient_name\"\n" +
                  "\"previous_consulted\"\n" +
                  "\"today\""}
                </Typography>
              </AccordionDetails>
            </Accordion>
             <TextField
              fullWidth
              label='Clinicians Prompt'
              multiline
              margin='dense'
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={cliniciansPrompt.error}
              error={!!cliniciansPrompt.error}
              value={cliniciansPrompt.value}
              onChange={({ target: { value } }) => setCliniciansPrompt({ value, error: '' })}
            />
            <Accordion sx={{ width: '100%' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel3-content"
                id="panel3-header"
              >
                <Typography>Variables for days offset</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word',
                  }}
                >
                {"\"today\"\n" +
                  "\"preferred_clinician\""}
                </Typography>
              </AccordionDetails>
            </Accordion>
            <TextField
              fullWidth
              label='Days Offset'
              multiline
              margin='dense'
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={offsetPrompt.error}
              error={!!offsetPrompt.error}
              value={offsetPrompt.value}
              onChange={({ target: { value } }) => setOffsetPrompt({ value, error: '' })}
            />
            <Accordion sx={{ width: '100%' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel4-content"
                id="panel4-header"
              >
                <Typography>Variables for booking prompt</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  sx={{
                    whiteSpace: 'pre-wrap', 
                    wordWrap: 'break-word', 
                  }}
                >
                  {"\"injury\"\n" +
                    "\"diagnosis\"\n" +
                    "\"injury_location\"\n" +
                    "\"fracture\"\n" +
                    "\"surgery\"\n" +
                    "\"acl\"\n" +
                    "\"joint_replacement\"\n" +
                    "\"spine_injury_location\"\n" +
                    "\"insurance_type\"\n" +
                    "\"insurance_carrier\"\n" +
                    "\"insurance_number\"\n" +
                    "\"preferred_clinician\"\n" +
                    "\"appointment_type\"\n" +
                    "\"age\"\n" +
                    "\"preferred_location\"\n" +
                    "\"patient_type\"\n" +
                    "\"patient_name\"\n" +
                    "\"previous_consulted\"\n" +
                    "\"booking_note\"\n" +
                    "\"today\""}
                </Typography>
              </AccordionDetails>
            </Accordion>
             <TextField
              fullWidth
              label='Booking Prompt'
              margin='dense'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={bookingPrompt.error}
              error={!!bookingPrompt.error}
              value={bookingPrompt.value}
              onChange={({ target: { value } }) => setBookingPrompt({ value, error: '' })}
            />
            <Accordion sx={{ width: '100%' }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel4-content"
                id="panel4-header"
              >
                <Typography>Variables for filter slots prompt</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  sx={{
                    whiteSpace: 'pre-wrap', 
                    wordWrap: 'break-word', 
                  }}
                >
                  {"\"injury\"\n" +
                    "\"diagnosis\"\n" +
                    "\"injury_location\"\n" +
                    "\"fracture\"\n" +
                    "\"surgery\"\n" +
                    "\"acl\"\n" +
                    "\"joint_replacement\"\n" +
                    "\"spine_injury_location\"\n" +
                    "\"insurance_type\"\n" +
                    "\"insurance_carrier\"\n" +
                    "\"insurance_number\"\n" +
                    "\"preferred_clinician\"\n" +
                    "\"appointment_type\"\n" +
                    "\"age\"\n" +
                    "\"clinician_full_name\"\n" +
                    "\"preferred_location\"\n" +
                    "\"patient_type\"\n" +
                    "\"patient_name\"\n" +
                    "\"previous_consulted\"\n" +
                    "\"booking_reason\"\n" +
                    "\"booking_type\"\n" +
                    "\"today\""}
                </Typography>
              </AccordionDetails>
            </Accordion>
             <TextField
              fullWidth
              label='Filter Slots Prompt'
              margin='dense'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={filterSlotsPrompt.error}
              error={!!filterSlotsPrompt.error}
              value={filterSlotsPrompt.value}
              onChange={({ target: { value } }) => setFilterSlotsPrompt({ value, error: '' })}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
              <Typography sx={{ m: '5px'}} variant='subtitle1'>
                Clinician filter prompt (from clinician's detail page) will run after the merge prompt
              </Typography>
              <Typography sx={{ m: '5px'}} variant='subtitle1'>
                Clinician prompt (from clinician's detail page) will run after clinician's filter prompt
              </Typography>
              <Typography sx={{ m: '5px'}} variant='subtitle1'>
                Clinician merge prompt (from clinician’s detail page) will run after clinician prompt
              </Typography>
            </Box>
            <TextField
              fullWidth
              label='Message to be sent, before sending the link'
              margin='dense'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={message.error}
              error={!!message.error}
              value={message.value}
              onChange={({ target: { value } }) => setMessage({ value, error: '' })}
            />
            <TextField
              fullWidth
              label='No slots message'
              margin='dense'
              multiline
              minRows={3}
              maxRows={10}
              disabled={fetching || (!!id && !edit)}
              helperText={noSlotsMessage.error}
              error={!!noSlotsMessage.error}
              value={noSlotsMessage.value}
              onChange={({ target: { value } }) => setNoSlotsMessage({ value, error: '' })}
            />
          </CardItem>
        </Card>
        <Card sx={{ p: 2, mt: 3 }}>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>History</Typography>
          <ScheduleAnalysesList />
        </Card>    
      </Container>
    </Box>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <ScheduleFlows {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);